// Migración de herramientas Access → pañoles usando ULTUBICA
// Ruta: /configuracion/migracion-access-htas
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Database, PlayCircle, Undo2, Loader2, CheckCircle2, AlertTriangle,
  ArrowRight, Sparkles, Package,
} from 'lucide-react';
import { toast } from 'sonner';

export default function MigracionAccessHtasPage() {
  const [preview, setPreview] = useState(null);
  const [panoles, setPanoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState({}); // raw → panol_id | "__skip__"
  const [crearFaltantes, setCrearFaltantes] = useState(false);
  const [minHtas, setMinHtas] = useState(5);
  const [executing, setExecuting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmUndoOpen, setConfirmUndoOpen] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pa] = await Promise.all([
        api.get('/bodega/migracion-access/preview'),
        api.get('/bodega/panoles?activos_solo=true'),
      ]);
      setPreview(p.data);
      setPanoles(pa.data.items || []);
      // Inicializar mapping con auto-match + persona móvil
      const init = {};
      (p.data.ubicaciones || []).forEach((u) => {
        if (u.es_persona_movil) init[u.raw] = '__persona_movil__';
        else if (u.panol_match_id) init[u.raw] = u.panol_match_id;
      });
      setMapping(init);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando preview');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const ejecutar = async (dryRun) => {
    setExecuting(true);
    try {
      const res = await api.post('/bodega/migracion-access/ejecutar', {
        dry_run: dryRun,
        crear_panoles_faltantes: crearFaltantes,
        min_htas_para_crear: minHtas,
        mapping_manual: mapping,
      });
      setLastResult({ ...res.data, was_dry_run: dryRun });
      if (dryRun) {
        toast.success(`Dry-run OK · ${res.data.resumen.total_htas_a_mover} htas simuladas`);
      } else {
        toast.success(`Migración aplicada · ${res.data.resumen.htas_insertadas} htas asignadas`);
        loadPreview();
      }
      setConfirmOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error ejecutando migración');
    } finally { setExecuting(false); }
  };

  const deshacer = async () => {
    setExecuting(true);
    try {
      const res = await api.post('/bodega/migracion-access/deshacer');
      toast.success(`Deshecho · ${res.data.cargos_borrados} cargos + ${res.data.panoles_borrados.length} pañoles borrados`);
      setLastResult(null);
      setConfirmUndoOpen(false);
      loadPreview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error deshaciendo');
    } finally { setExecuting(false); }
  };

  if (loading || !preview) {
    return <div className="p-6 space-y-3"><Skeleton className="h-32 w-full" /><Skeleton className="h-64 w-full" /></div>;
  }

  const ubicaciones = preview.ubicaciones || [];
  const htasConMapping = ubicaciones.reduce((acc, u) => {
    const m = mapping[u.raw];
    return acc + (m && m !== '__skip__' ? u.count : 0);
  }, 0);
  const htasPersonaMovil = ubicaciones.reduce((acc, u) => {
    return acc + (mapping[u.raw] === '__persona_movil__' ? u.count : 0);
  }, 0);
  const htasSkip = ubicaciones.reduce((acc, u) => {
    return acc + (mapping[u.raw] === '__skip__' ? u.count : 0);
  }, 0);
  const htasSinResolver = preview.total_htas_a_migrar - htasConMapping - htasSkip;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="migracion-access-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Database className="w-6 h-6 text-indigo-600" />
          Migración Access → Pañoles (ULTUBICA)
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Auto-asigna las herramientas importadas del Access a pañoles usando el campo ULTUBICA.
          Puedes ajustar el mapeo antes de ejecutar.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card><CardContent className="p-4 text-center">
          <div className="text-3xl font-bold text-indigo-700" data-testid="stat-total">
            {preview.total_htas_a_migrar.toLocaleString('es-CL')}
          </div>
          <div className="text-xs text-slate-500">Htas a migrar</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-3xl font-bold text-slate-700">
            {preview.total_ubicaciones_distintas}
          </div>
          <div className="text-xs text-slate-500">Ubicaciones distintas</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-3xl font-bold text-emerald-700">
            {(htasConMapping - htasPersonaMovil).toLocaleString('es-CL')}
          </div>
          <div className="text-xs text-slate-500">🏭 Irán a pañoles</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-3xl font-bold text-amber-700" data-testid="stat-persona-movil">
            {htasPersonaMovil.toLocaleString('es-CL')}
          </div>
          <div className="text-xs text-slate-500">👤 Personas móviles</div>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <div className="text-3xl font-bold text-rose-700" data-testid="stat-ya-migradas">
            {preview.total_htas_ya_migradas.toLocaleString('es-CL')}
          </div>
          <div className="text-xs text-slate-500">Ya migradas (previo)</div>
        </CardContent></Card>
      </div>

      {/* Configuración */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Configuración de ejecución</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="chk-crear"
              checked={crearFaltantes}
              onCheckedChange={(v) => setCrearFaltantes(!!v)}
              data-testid="chk-crear-faltantes"
            />
            <label htmlFor="chk-crear" className="text-sm cursor-pointer">
              <b>Auto-crear pañoles faltantes</b> para ubicaciones sin match
            </label>
            {crearFaltantes && (
              <div className="flex items-center gap-2 ml-4">
                <span className="text-xs text-slate-500">mínimo htas para crear:</span>
                <Input
                  type="number" min={1} max={100}
                  value={minHtas} onChange={(e) => setMinHtas(parseInt(e.target.value, 10) || 5)}
                  className="w-20"
                  data-testid="input-min-htas"
                />
              </div>
            )}
          </div>
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
            <AlertTriangle className="inline w-3 h-3 mr-1" />
            {htasSinResolver > 0 && `${htasSinResolver} htas quedarán fuera (sin destino asignado). `}
            {htasSkip > 0 && `${htasSkip} marcadas como "saltar". `}
            {htasSinResolver === 0 && htasSkip === 0 && 'Todas las htas tienen destino resuelto.'}
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button
              variant="outline" onClick={() => ejecutar(true)} disabled={executing}
              data-testid="btn-dry-run"
            >
              {executing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Simular (dry-run)
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)} disabled={executing || htasConMapping === 0}
              data-testid="btn-ejecutar"
            >
              <PlayCircle className="w-4 h-4 mr-1" /> Ejecutar migración definitiva
            </Button>
            {preview.total_htas_ya_migradas > 0 && (
              <Button
                variant="destructive" onClick={() => setConfirmUndoOpen(true)} disabled={executing}
                data-testid="btn-deshacer"
              >
                <Undo2 className="w-4 h-4 mr-1" /> Deshacer migración
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabla ubicaciones */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Plan de migración · {ubicaciones.length} ubicaciones</CardTitle>
          <p className="text-xs text-muted-foreground">
            Ajusta el destino de cada ubicación si el auto-match no es correcto. "Saltar" omite esas htas de la migración.
          </p>
        </CardHeader>
        <CardContent>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-2 py-2">Ubicación raw (Access)</th>
                  <th className="text-left px-2 py-2">Parsed</th>
                  <th className="text-left px-2 py-2">Responsable</th>
                  <th className="text-center px-2 py-2">Htas</th>
                  <th className="text-center px-2 py-2">Tipo</th>
                  <th className="text-center px-2 py-2 w-5"></th>
                  <th className="text-left px-2 py-2">Destino</th>
                </tr>
              </thead>
              <tbody>
                {ubicaciones.map((u) => {
                  const sel = mapping[u.raw];
                  const effectiveSel = sel === undefined
                    ? (u.es_persona_movil ? '__persona_movil__' : (u.panol_match_id || ''))
                    : sel;
                  const isPersona = effectiveSel === '__persona_movil__';
                  const isSkip = effectiveSel === '__skip__';
                  const resolved = !!effectiveSel && !isSkip;
                  return (
                    <tr key={u.raw} className="border-t hover:bg-slate-50" data-testid={`row-ubic-${u.raw.slice(0, 20).replace(/\W/g, '-')}`}>
                      <td className="px-2 py-1 font-mono text-[11px] max-w-[260px] truncate" title={u.raw}>
                        {u.raw}
                      </td>
                      <td className="px-2 py-1 font-semibold">{u.ubicacion_parsed || '—'}</td>
                      <td className="px-2 py-1 text-slate-600 max-w-[150px] truncate" title={u.responsable_parsed}>
                        {u.responsable_parsed || '—'}
                      </td>
                      <td className="px-2 py-1 text-center font-bold">{u.count}</td>
                      <td className="px-2 py-1 text-center">
                        {u.es_persona_movil ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">👤 Persona</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">🏭 Pañol</Badge>
                        )}
                      </td>
                      <td className="px-2 py-1 text-center">
                        {resolved && <ArrowRight className="w-3 h-3 text-emerald-600" />}
                      </td>
                      <td className="px-2 py-1">
                        <select
                          value={effectiveSel}
                          onChange={(e) => setMapping({ ...mapping, [u.raw]: e.target.value })}
                          className={`border rounded px-2 py-1 text-xs w-full ${
                            isPersona ? 'bg-amber-50 border-amber-300'
                              : isSkip ? 'bg-slate-100'
                              : resolved ? 'bg-emerald-50 border-emerald-300'
                              : 'bg-rose-50 border-rose-300'
                          }`}
                          data-testid={`select-destino-${u.raw.slice(0, 20).replace(/\W/g, '-')}`}
                        >
                          <option value="">— sin asignar —</option>
                          <option value="__persona_movil__">👤 Persona móvil (sin pañol)</option>
                          <option value="__skip__">⊘ Saltar</option>
                          <optgroup label="Pañoles existentes">
                            {panoles.map((p) => (
                              <option key={p.id} value={p.id}>{p.nombre}</option>
                            ))}
                          </optgroup>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Último resultado */}
      {lastResult && (
        <Card className={lastResult.dry_run ? 'border-indigo-300' : 'border-emerald-300'} data-testid="card-resultado">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              {lastResult.dry_run ? <Sparkles className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
              {lastResult.dry_run ? 'Simulación (dry-run)' : 'Migración ejecutada'}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><b>{lastResult.resumen.ubicaciones_procesadas}</b> ubicaciones</div>
              <div><b>{lastResult.resumen.total_htas_a_mover}</b> htas a mover</div>
              <div><b className="text-amber-700">{lastResult.resumen.htas_saltadas}</b> saltadas</div>
              <div><b className="text-emerald-700">{lastResult.resumen.htas_insertadas}</b> insertadas</div>
            </div>
            {lastResult.resumen.panoles_creados?.length > 0 && (
              <div>
                <div className="text-xs font-semibold">Pañoles creados ({lastResult.resumen.panoles_creados.length}):</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {lastResult.resumen.panoles_creados.map((p) => (
                    <Badge key={p.id} variant="outline" className="text-[10px]">{p.nombre}</Badge>
                  ))}
                </div>
              </div>
            )}
            {lastResult.resumen.panoles_faltantes_requeridos?.length > 0 && !lastResult.resumen.panoles_creados?.length && (
              <div>
                <div className="text-xs font-semibold text-amber-700">
                  Ubicaciones que requieren pañol (marca "auto-crear" para crearlos):
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {lastResult.resumen.panoles_faltantes_requeridos.map((n) => (
                    <Badge key={n} variant="outline" className="text-[10px] border-amber-300 text-amber-700">{n}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmar ejecución */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Ejecutar migración DEFINITIVA?</AlertDialogTitle>
            <AlertDialogDescription>
              Se crearán <b>{htasConMapping}</b> entries en <code>herramientas_cargo</code>.
              <br /><b>Esta acción es reversible</b> con el botón "Deshacer migración" (marcamos con origen_migracion).
              <br /><br />¿Continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => ejecutar(false)} data-testid="btn-confirm-ejecutar">
              Sí, ejecutar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar deshacer */}
      <AlertDialog open={confirmUndoOpen} onOpenChange={setConfirmUndoOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Deshacer la migración?</AlertDialogTitle>
            <AlertDialogDescription>
              Se borrarán TODOS los cargos con <code>origen_migracion=access_ultubica</code>
              ({preview.total_htas_ya_migradas} htas).
              <br />Los pañoles auto-creados que no tengan otros usos también se borrarán.
              <br /><br /><b className="text-rose-700">Esta acción no puede deshacerse.</b>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={deshacer} className="bg-rose-600 hover:bg-rose-700"
              data-testid="btn-confirm-deshacer"
            >
              Sí, deshacer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
